import type { Api } from "../../../services/api";
import type { ArticleDetail } from "../hooks/useArticleData";

type ArticleListItem =
  Api["get"]["/webapp/public/articles"]["output"]["body"]["docs"][number];

/** The sections an article's narration reads, in the order it reads them. */
export type SpeechSectionId =
  | "background"
  | "objectives"
  | "methods"
  | "results"
  | "limitations"
  | "conclusion";

type SpeechSection = {
  id: SpeechSectionId;
  /** Share of the narration's length, in (0, 1]; the shares sum to 1. */
  share: number;
};

// Narration pace of the TTS voices, for the "Listen · 2 min" figure shown
// before the audio exists and its real length is known.
const NARRATION_WORDS_PER_MINUTE = 150;

/**
 * The text the narration reads, section by section: the same fields in the
 * same order as `buildSpeechText` in apps/api/src/service/speech.ts, each
 * after its spoken header. The API stores one audio file with no timings, so
 * this text is how the player knows what it is reading.
 */
function narratedTexts(
  article: ArticleDetail | ArticleListItem
): { id: SpeechSectionId; text: string }[] {
  const entries: { id: SpeechSectionId; header: string; text: string }[] = [
    {
      id: "background",
      header: "background",
      text: article.background === null ? "" : article.background,
    },
    {
      id: "objectives",
      header: "objectives",
      text: article.objectives.filter((line) => line !== "").join("\n"),
    },
    {
      id: "methods",
      header: "methodology",
      text: article.methodology === null ? "" : article.methodology,
    },
    {
      id: "results",
      header: "results",
      text: article.results.filter((line) => line !== "").join("\n"),
    },
    {
      id: "limitations",
      header: "limitations",
      text: article.limitations === null ? "" : article.limitations,
    },
    {
      id: "conclusion",
      header: "conclusion",
      text: article.conclusion.filter((line) => line !== "").join("\n"),
    },
  ];
  return entries
    .filter((entry) => entry.text.trim() !== "")
    .map((entry) => ({
      id: entry.id,
      text: `${entry.header}:\n${entry.text}`,
    }));
}

/**
 * Each narrated section's share of the audio, estimated from its share of
 * the text: a TTS voice keeps a steady pace, so a section lasts about as long
 * as its text is long. Empty when the article has nothing to narrate.
 */
export function speechSections(
  article: ArticleDetail | ArticleListItem
): SpeechSection[] {
  const texts = narratedTexts(article);
  const total = texts.reduce((sum, entry) => sum + entry.text.length, 0);
  if (total === 0) return [];
  return texts.map((entry) => ({
    id: entry.id,
    share: entry.text.length / total,
  }));
}

/** The section being read at `fraction` (0 to 1) of the narration. */
export function sectionAt(
  sections: SpeechSection[],
  fraction: number
): SpeechSectionId | null {
  let end = 0;
  for (const section of sections) {
    end += section.share;
    if (fraction < end) return section.id;
  }
  const last = sections.at(-1);
  return last === undefined ? null : last.id;
}

/** How long the narration should last, in whole minutes, before it exists. */
export function estimatedListenMinutes(
  article: ArticleDetail | ArticleListItem
): number {
  const words = narratedTexts(article)
    .map((entry) => entry.text)
    .join(" ")
    .split(/\s+/)
    .filter((word) => word !== "").length;
  return Math.max(1, Math.round(words / NARRATION_WORDS_PER_MINUTE));
}
